<script setup>
import { onMounted, onUnmounted, watch, ref } from 'vue'
import { useUser } from '@/store/user.js'
import { useSettings } from '@/store/settings.js'
import { storeToRefs } from 'pinia'

const props = defineProps({
  id: {
    type: String,
    default: 'freshchat',
  },
  source: {
    type: String,
    default: '//fw-cdn.com/11515865/4173454.js',
  },
})

const CHAT_ENTRY_PROPERTY = 'cf_chat_entry'
const CHAT_ENTRY_VALUE_APP = 'app'

const user = useUser()
const { profile, region: userRegion } = storeToRefs(user)
const { settings } = useSettings()

// Store event handler references for cleanup
const eventHandlers = ref({
  widgetLoaded: null,
  widgetOpened: null,
  userCreated: null,
  userCleared: null,
})

/**
 * Set conversation-level source (visible in inbox Conversation Properties).
 * Works for anonymous and logged-in users per Freshchat Web SDK.
 */
const applyConversationSource = () => {
  const fc = window.fcWidget
  if (
    !fc ||
    !fc.conversation ||
    typeof fc.conversation.setConversationProperties !== 'function'
  ) {
    // eslint-disable-next-line no-restricted-syntax
    console.log('applyConversationSource: conversation API not ready')
    return
  }
  const payload = { [CHAT_ENTRY_PROPERTY]: CHAT_ENTRY_VALUE_APP }
  fc.conversation.setConversationProperties(payload)
}

/**
 * Set license region on Freshchat contact (works for anonymous and logged-in users).
 */
const applyLicense = () => {
  const fc = window.fcWidget
  // After login use user.region (authoritative source from /v2/user),
  // fallback to settings.region for unauthenticated context (from /v2/info).
  const region = userRegion.value || settings.region
  if (!fc?.user || typeof fc.user.update !== 'function' || !region) return

  fc.user.update({
    meta: { cf_license: region.toUpperCase() },
  })
}

/**
 * Update user profile data in Freshchat
 */
const updateProfileData = () => {
  /**
   * @typedef {Object} FreshchatWidget
   * @property {function(string): void} setExternalId - Sets external user ID
   * @property {Object} user - User object
   * @property {function(Object): void} user.update - Updates user data
   */

  /**
   * @type FreshchatWidget
   */
  const fc = window.fcWidget
  if (fc && profile.value.id) {
    // Set external ID
    if (typeof fc.setExternalId === 'function') {
      fc.setExternalId(profile.value.id)
    }

    // Update user data using update method
    if (fc.user && typeof fc.user.update === 'function') {
      const payload = {}

      if (profile.value.email) payload.email = profile.value.email
      if (profile.value.username) payload.firstName = profile.value.username

      const meta = {}
      if (profile.value.company) meta.cf_company_name = profile.value.company
      if (profile.value.phone) meta.phone = profile.value.phone

      if (Object.keys(meta).length > 0) payload.meta = meta

      if (Object.keys(payload).length > 0) {
        fc.user.update(payload)
      }
    }
  }
}

/**
 * Handle user logout
 */
const handleLogout = () => {
  if (window.fcWidget && window.fcWidget.user) {
    window.fcWidget.user.clear()
    if (
      typeof window.fcWidget.on === 'function' &&
      !eventHandlers.value.userCleared
    ) {
      eventHandlers.value.userCleared = () => {
        window.fcWidget.close()
      }
      window.fcWidget.on('user:cleared', eventHandlers.value.userCleared)
    }
  }
}

watch(
  profile,
  (newVal, oldVal) => {
    if (!newVal.id && oldVal.id) {
      // Logout
      handleLogout()
    }

    updateProfileData()
    applyLicense()
  },
  { deep: true },
)

watch([userRegion, () => settings.region], () => applyLicense())

onMounted(() => {
  // Set Freshchat settings BEFORE loading the script
  window.fcSettings = {
    onInit: function () {
      // Set up widget:loaded event handler
      if (window.fcWidget && typeof window.fcWidget.on === 'function') {
        eventHandlers.value.widgetLoaded = function () {
          applyConversationSource()
          applyLicense()
          updateProfileData()
        }
        window.fcWidget.on('widget:loaded', eventHandlers.value.widgetLoaded)

        eventHandlers.value.widgetOpened = function () {
          applyConversationSource()
          applyLicense()
        }
        window.fcWidget.on('widget:opened', eventHandlers.value.widgetOpened)

        // Set up user:created event handler
        eventHandlers.value.userCreated = function () {
          applyConversationSource()
          applyLicense()
          updateProfileData()
        }
        window.fcWidget.on('user:created', eventHandlers.value.userCreated)
      }

      applyConversationSource()
      applyLicense()
      updateProfileData()
    },
  }

  // Create and load the Freshchat script
  const script = document.createElement('script')
  script.setAttribute('chat', 'true')
  script.src = props.source
  script.id = props.id

  document.body.appendChild(script)
})

onUnmounted(() => {
  // Unsubscribe from all event listeners
  if (window.fcWidget && typeof window.fcWidget.off === 'function') {
    if (eventHandlers.value.widgetLoaded) {
      window.fcWidget.off('widget:loaded', eventHandlers.value.widgetLoaded)
    }
    if (eventHandlers.value.widgetOpened) {
      window.fcWidget.off('widget:opened', eventHandlers.value.widgetOpened)
    }
    if (eventHandlers.value.userCreated) {
      window.fcWidget.off('user:created', eventHandlers.value.userCreated)
    }
    if (eventHandlers.value.userCleared) {
      window.fcWidget.off('user:cleared', eventHandlers.value.userCleared)
    }
  }

  // Remove the script element
  const scriptEl = document.querySelector('#' + props.id)
  if (scriptEl) {
    scriptEl.remove()
  }
})
</script>

<style lang="scss">
#fc_frame {
  position: fixed;
}
</style>
